import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';

// Brand typography, self-hosted. @fontsource ships the woff2 files through the
// bundler, so vite-plugin-pwa precaches them and an offline PWA open still
// renders IBM Plex instead of falling back to a system face.
//
// Latin + latin-ext subsets only. The bare `400.css` entrypoints pull every
// subset IBM Plex ships (cyrillic, greek, vietnamese), which took the PWA
// precache from ~0.8 MB to 2.3 MB — a real cost for crew installing this over
// a stadium connection. latin-ext keeps the accented characters European crew
// names actually need.
import '@fontsource/ibm-plex-sans/latin-400.css';
import '@fontsource/ibm-plex-sans/latin-500.css';
import '@fontsource/ibm-plex-sans/latin-600.css';
import '@fontsource/ibm-plex-sans/latin-700.css';
import '@fontsource/ibm-plex-sans/latin-ext-400.css';
import '@fontsource/ibm-plex-sans/latin-ext-500.css';
import '@fontsource/ibm-plex-sans/latin-ext-600.css';
import '@fontsource/ibm-plex-sans/latin-ext-700.css';
import '@fontsource/ibm-plex-mono/latin-400.css';
import '@fontsource/ibm-plex-mono/latin-500.css';
import '@fontsource/ibm-plex-mono/latin-600.css';

import './index.css';
import { registerSW } from 'virtual:pwa-register';

// Service worker: auto-updating. A new deploy takes over silently; sessions
// that were open mid-deploy keep working on the old assets until reload.
if ('serviceWorker' in navigator) {
  registerSW({ immediate: true });
}

// Capture beforeinstallprompt AT MODULE LOAD. The browser fires it during
// initial page load — long before any React component mounts — so a
// component-mounted listener misses it every time (this exact bug shipped:
// the install card never appeared on Android/desktop). Stash the event
// globally; InstallPrompt reads the stash and also listens for late fires.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as unknown as { __pwaInstallEvent?: Event }).__pwaInstallEvent = e;
  window.dispatchEvent(new CustomEvent('pwa-install-available'));
});

// Get the root element
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

// Create React root and render app
createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);
